<template>
  <StandardCardPageContainer :include-navigation="true">
    <template #title>Create Questionnaire</template>

    <div class="text-left">
      <v-row>
        <v-col cols="12">
          <PInputField
            :id="'create-questionnaire-view__questionnaire-name'"
            :value="title"
            :label="'Give your questionnaire a short and clear title'"
            :placeholder="'Questionnaire name...'"
            :error="v$.title.$error"
            :error-messages="v$.title.$errors[0]?.$message.toString()"
            @update-event="(val) => (title = val)"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <p>Choose questionnaire template</p>
          <PAutocomplete
            id="create-questionnaire-view__select-template"
            v-model="selectedLabel"
            :items="templateLabels"
            :select-text="selectedLabel"
            placeholder="Select template"
            :error="v$.selectedLabel.$error"
            :error-messages="v$.selectedLabel.$errors[0]?.$message.toString()"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-expansion-panels multiple>
            <v-expansion-panel
              id="create-questionnaire-view__view-template-panel"
              elevation="0"
            >
              <v-expansion-panel-title
                expand-icon="mdi-plus"
                collapse-icon="mdi-minus"
              >
                <p class="text-left">View template questions</p>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <DisplayQuestionnaire
                  v-if="selectedLabel"
                  :template-label="selectedLabel"
                  :key="selectedLabel"
                />
                <p v-else id="create-questionnaire-view__template-not-selected">
                  Select a questionnaire template to view the questions
                </p>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel elevation="0">
              <v-expansion-panel-title
                expand-icon="mdi-plus"
                collapse-icon="mdi-minus"
              >
                <p class="text-left">View email content</p>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row align="center">
                  <v-col cols="5">
                    <v-autocomplete
                      v-model="selectedReceiver"
                      :items="receiverItems"
                      @update:model-value="getEmail"
                      density="compact"
                      variant="underlined"
                    >
                      <template v-slot:prepend>
                        <p class="mt-2">Email sent to</p>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
                <v-divider></v-divider>
                <v-row>
                  <v-col>
                    <p class="mt-5" v-dompurify-html:email="email"></p>
                  </v-col>
                </v-row>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </div>

    <template #nextButton>
      <PPrimaryButton
        id="create-questionnaire-view__create"
        :loading="creatingQuestionnaire"
        @click="createQuestionnaireFromTemplate(selectedLabel)"
        >Create
      </PPrimaryButton>
    </template>
  </StandardCardPageContainer>
</template>

<script setup lang="ts">
import PAutocomplete from "@/components/custom/PAutocomplete.vue";
import PPrimaryButton from "@/components/custom/buttons/PPrimaryButton.vue";
import PSecondaryButton from "@/components/custom/buttons/PSecondaryButton.vue";
import StandardCardPageContainer from "@/components/containers/StandardCardPageContainer.vue";
import type { QuestionnaireTemplateInfoDto } from "@/models/dto/questionnaireTemplateInfoDto";
import { RouterPath } from "@/models/enum/routerPath";
import { createQuestionnaire } from "@/services/questionnaireService";
import { getTemplates } from "@/services/templateService";
import useVuelidate from "@vuelidate/core";
import { maxLength, required } from "@vuelidate/validators";
import { computed, onBeforeMount, ref } from "vue";
import { useRouter } from "vue-router";
import DisplayQuestionnaire from "../template/DisplayQuestionnaire.vue";
import { getCustomerEmail } from "@/services/emailService";
import { useUserProfileStore } from "@/stores/userProfileStore";
import PInputField from "@/components/custom/PInputField.vue";
import StandardEmptyPageContainer from "@/components/containers/StandardEmptyPageContainer.vue";
import PBackButton from "@/components/custom/buttons/PBackButton.vue";

const emit = defineEmits<{
  (e: "create-questionnaire", template: QuestionnaireTemplateInfoDto): void;
}>();

const router = useRouter();
const userProfileStore = useUserProfileStore();

const loadingPage = ref(true);
const creatingQuestionnaire = ref(false);
const activeError = ref(false);
const showBusinessPartnerEmail = ref(false);

const questionnaireTemplates = ref(new Array<QuestionnaireTemplateInfoDto>());
const templateLabels = computed(() =>
  questionnaireTemplates.value.map((q) => q.label)
);
const selectedLabel = ref("");
const title = ref("");
const email = ref("");
const selectedCompany = computed(() => userProfileStore.trustGroup.company);
const receiverItems = ["Suppliers", "Business Partners"];
const selectedReceiver = ref(receiverItems[0]);

const rules = computed(() => ({
  title: {
    required,
    maxLength: maxLength(50),
  },
  selectedLabel: { required },
}));

const v$ = useVuelidate(rules, { title, selectedLabel });

onBeforeMount(async () => {
  try {
    loadingPage.value = true;
    questionnaireTemplates.value = await getTemplates();
    email.value = await getCustomerEmail(
      selectedCompany.value.companyName,
      showBusinessPartnerEmail.value
    );
  } catch (error) {
    activeError.value = true;
  } finally {
    loadingPage.value = false;
  }
});

async function getEmail() {
  showBusinessPartnerEmail.value =
    selectedReceiver.value == "Business Partners";
  try {
    email.value = await getCustomerEmail(
      selectedCompany.value.companyName,
      showBusinessPartnerEmail.value
    );
  } catch (error) {
    activeError.value = true;
  }
}

async function createQuestionnaireFromTemplate(label: string): Promise<void> {
  const isValid = await v$.value.$validate();
  if (isValid) {
    const template = questionnaireTemplates.value.find(
      (t) => t.label === label
    );
    if (template != null) {
      try {
        creatingQuestionnaire.value = true;
        await createQuestionnaire(template.id, title.value);
        emit("create-questionnaire", template);
      } finally {
        creatingQuestionnaire.value = false;
        router.push(RouterPath.Questionnaires);
      }
    }
    v$.value.$reset();
  }
}
</script>
